import Component from '../../../types/common/Component';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import Operand from '../../../types/functions/Operand/Operand';
import ComponentType from '../../../types/report/ComponentType';
import Mode from '../../../types/report/Mode';
import ReportConfig from '../../../types/report/ReportConfig';
import UserDefinedFunction from '../../../types/report/UserDefinedFunction';
import dataType from '../../../infrastructure/constants/dataType';
import operandType from '../../../infrastructure/constants/functions/operandType';
import { reportType } from '../../../infrastructure/constants/reportType';
import clear from '../../../infrastructure/helpers/common/array/clear';
import assignmentHelper from '../../../infrastructure/helpers/functions/assignment/assignmentHelper';
import operandHelper from '../../../infrastructure/helpers/functions/operand/operandHelper';
import CustomDropdownOption from '../../Common/Dropdown/CustomDropdownOption';
import copyObject from '../../../infrastructure/helpers/common/copyObject';
import ReportCollections from '../../../infrastructure/types/Functions/ReportCollections';
import reportLogicReferenceHelper from '../../../infrastructure/helpers/functions/report/reportLogicReferenceHelper';
import referenceHelper from '../../Common/Modals/ReferencesModal/ReferenceModal.Helpers';
import { buildText } from '../Common/ReferenceComponentHelper/ReferenceComponentHelper';

const add = (functions: UserDefinedFunction[], func: UserDefinedFunction): void => {

    let lastNumber = functions.length > 0 ? functions[functions.length - 1].number : 0;

    func.number = lastNumber + 1;

    func.name = func.name || `function-${func.number}`;

    func.errors = [];

    func.globalUDFModuleId = func.globalUDFModuleId ? func.globalUDFModuleId : func.id || 0;

    func.id = null;

    func.parameters.forEach((p) => p.errors = []);

    functions.push(func);
};

const get = (functions: UserDefinedFunction[], location: ReportLogicLocation): UserDefinedFunction => {

    return getByNumber(functions, location.statementNumber);
};

const getDefault = (): UserDefinedFunction => {

    let result = {} as UserDefinedFunction;

    result.id = null;
    result.name = '';
    result.number = 0;
    result.returnType = dataType.string;
    result.businessDescription = '';
    result.parameters = [];
    result.assignment = assignmentHelper.getEmpty(false);
    result.errors = [];
    result.mode = Mode.Local

    reset(result);

    return result;
};

const reset = (func: UserDefinedFunction): void => {

    clear(func.parameters);
};

const getName = (func: UserDefinedFunction): string => {

    return `UDF${func.number}-${func.name}`;
}

const getByNumber = (statements: UserDefinedFunction[], number: number | null): UserDefinedFunction => {

    return statements.find(x => x.number === number) as UserDefinedFunction;
}

const getByNumberAndMode = (statements: UserDefinedFunction[], number: number | null, mode: string): UserDefinedFunction => {

    return statements.find(x => x.mode === mode && x.number === number) as UserDefinedFunction;
}

const findUserDefinedFunctionReferences = (userDefinedFunctionNumber: number, reportConfig: ReportConfig, globalReferences: Component[]): Map<ComponentType, Set<string>> => {

    let references = new Map<ComponentType, Set<string>>();

    operandHelper.traverseReportConfigSectionsForOperands(reportConfig, (operand: Operand, itemName?: string, componentType?: ComponentType) => {

        if (operand?.functionNumber === userDefinedFunctionNumber) {

            let values: Set<string> = references.get(componentType as ComponentType) || new Set();

            values.add(itemName as string);

            references.set(componentType as ComponentType, values);
        }
    });

    if (reportConfig.type === reportType.globalModule) {

        let func = reportConfig.userDefinedFunctions.find(x => x.number === userDefinedFunctionNumber);

        let id = func?.id as number;

        globalReferences.filter(x => x.referenceId === id).forEach((refer) => {

            let values = references.get(refer.componentType) || new Set();

            values.add(`Global ${refer.name}`);

            references.set(refer.componentType, values);
        });
    }

    return references;
}

const findGlobalUserDefinedFunctionsReferences = (reportConfig: ReportConfig, collections: ReportCollections) => {

    let functionNumbers = new Set<number>();

    operandHelper.traverseReportConfigSectionsForOperands(reportConfig, (operand: Operand) => {

        if (operand?.operandType === operandType.globalUserDefinedFunction) {

            functionNumbers.add(operand.functionNumber as number);
        }
    });

    return Array.from(functionNumbers);
}

const getUdfReferences = async (number: number, reportConfig: ReportConfig, collections: ReportCollections): Promise<CustomDropdownOption[]> => {

    let config = copyObject(reportConfig);

    config.userDefinedFunctions = config.userDefinedFunctions.filter(x => x.number !== number);

    let references = reportLogicReferenceHelper.getUserDefinedFunctionReferences(number, config, collections);

    let options: CustomDropdownOption[] = references.map((x) => { return { component: { htmlId: x.value, name: x.text, componentType: x.componentType, componentNumber: x.number, reportConfigId: x.configId, componentInnerType: x.innerType, mainComponentNumber: x.mainComponentNumber } as Component } as CustomDropdownOption });

    if (config.type === reportType.globalModule) {

        let udf = reportConfig.userDefinedFunctions.find(x => x.number === number);

        if (udf?.id) {

            let component = { componentInnerType: ComponentType.UserDefinedFunction.replaceAll(' ', ''), componentType: ComponentType.UserDefinedFunction.replaceAll(' ', ''), componentId: udf?.id, reportConfigId: config.id, componentNumber: udf?.number } as Component;

            let refs = await referenceHelper.getGlobalReferences(component);

            refs.forEach((ref) => {
                ref.name = buildText(ref.reportConfigName || '', ref.name || '', ref.componentType);
                options.push({
                    component: ref,
                    asUrl: true
                } as CustomDropdownOption)
            });
        }
    }

    return options;
}

const getByName=(functions:UserDefinedFunction[],functionName:string)=>{
    return functions.find(x=>x.name===functionName) || {} as UserDefinedFunction
}

const userDefinedFunctionHelper = {
    add,
    get,
    getDefault,
    reset,
    getName,
    getByNumber,
    getByNumberAndMode,
    findUserDefinedFunctionReferences,
    findGlobalUserDefinedFunctionsReferences,
    getUdfReferences,
    getByName
};

export default userDefinedFunctionHelper;
